import { TFunction } from 'i18next'

import { DAY_NAMES_NARROW } from '@/constants/date'
import type { PackageSettings } from '@/models'

import type { LocationItem, PackagesSettingsForm, TabKey } from './packages-settings.types'

export const convertLocations = (apiLocations: PackageSettings['locations']): LocationItem[] => {
  const sorted = [...apiLocations].sort((locationA, locationB) => {
    const isRoomA = (locationA as LocationItem).packageRoom
    const isRoomB = (locationB as LocationItem).packageRoom
    if (isRoomA) return -1
    if (isRoomB) return 1
    return 0
  })

  return sorted.map((location, index) => ({
    id: index + 1,
    name: location.name,
    packageRoom: (location as LocationItem).packageRoom,
  }))
}

export const getTabOptions = (
  t: TFunction<['packages-settings']>,
  packageRoomAvailable: boolean,
): Array<{ label: string; value: TabKey }> => [
  { label: t('packages-settings:tabs.general'), value: 'general' },
  ...(packageRoomAvailable
    ? [{ label: t('packages-settings:tabs.packageRoom'), value: 'package-room' as TabKey }]
    : []),
  { label: t('packages-settings:tabs.locations'), value: 'locations' },
  { label: t('packages-settings:tabs.carriers'), value: 'carriers' },
]

export const mapSettingsToForm = (apiSettings: PackageSettings): PackagesSettingsForm => ({
  allowASAP: apiSettings.allowASAP,
  allowQuickScan: apiSettings.allowQuickScan,
  businessHours: DAY_NAMES_NARROW.map((day) => {
    const existing = apiSettings.businessHours.find((h) => h.name === day)
    return {
      enabled: !!existing,
      endTime: existing?.endTime ?? 1200,
      name: day,
      startTime: existing?.startTime ?? 480,
    }
  }),
  carrierAccessCodeEnabled: apiSettings.carrierAccessCodeEnabled ?? false,
  carriers: apiSettings.carriers?.map((carrier) => ({ name: carrier.name })) ?? [],
  customLocationsEnabled: apiSettings.locations.length > 0,
  d2dMode: apiSettings.d2dMode,
  deliverySlotLength: apiSettings.deliverySlotLength,
  locationRequired: apiSettings.locationRequired,
  locations: apiSettings.locations.length
    ? convertLocations(apiSettings.locations as PackageSettings['locations'])
    : [{ id: 1, name: '' }],
  maskUsernameEnabled: apiSettings.maskUsernameEnabled ?? false,
  maxDeliveriesPerSlot: apiSettings.maxDeliveriesPerSlot,
  photoRequired: apiSettings.photoRequired,
  trackingNumberRequired: apiSettings.trackingNumberRequired,
})
